import React, { useEffect, useRef, useState } from 'react';
import { FiMove } from 'react-icons/fi';

const MIN_CROP_SPAN_PERCENT = 10;
const EMPTY_CROP_RECT = { top: 0, right: 0, bottom: 0, left: 0 };

const clampPercent = (value, min = 0, max = 100) => (
  Math.min(max, Math.max(min, Number.isFinite(value) ? value : min))
);

const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    if (typeof reader.result === 'string' && reader.result) resolve(reader.result);
    else reject(new Error('촬영한 이미지를 읽지 못했습니다.'));
  };
  reader.onerror = () => reject(new Error('촬영한 이미지를 읽지 못했습니다.'));
  reader.readAsDataURL(file);
});

const loadImage = (sourceUrl) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error('이미지 형식을 처리할 수 없습니다.'));
  image.src = sourceUrl;
});

const canvasToBlob = (canvas) => new Promise((resolve, reject) => {
  canvas.toBlob((blob) => {
    if (blob) resolve(blob);
    else reject(new Error('선택한 영역을 이미지로 만들지 못했습니다.'));
  }, 'image/jpeg', 0.93);
});

const cropImage = async (sourceUrl, cropRect, originalName) => {
  const image = await loadImage(sourceUrl);
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;
  const sourceX = Math.round((width * cropRect.left) / 100);
  const sourceY = Math.round((height * cropRect.top) / 100);
  const cropWidth = Math.max(1, Math.round((width * (100 - cropRect.left - cropRect.right)) / 100));
  const cropHeight = Math.max(1, Math.round((height * (100 - cropRect.top - cropRect.bottom)) / 100));
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) throw new Error('이 브라우저에서는 이미지를 자를 수 없습니다.');
  canvas.width = cropWidth;
  canvas.height = cropHeight;
  context.fillStyle = '#fff';
  context.fillRect(0, 0, cropWidth, cropHeight);
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(
    image,
    sourceX,
    sourceY,
    cropWidth,
    cropHeight,
    0,
    0,
    cropWidth,
    cropHeight
  );

  const blob = await canvasToBlob(canvas);
  const baseName = String(originalName || 'answer-sheet').replace(/\.[^.]+$/, '') || 'answer-sheet';
  return new File([blob], `${baseName}-cropped.jpg`, {
    type: 'image/jpeg',
    lastModified: Date.now()
  });
};

const AnswerSheetCropModal = ({ file, onCancel, onApply, onProcessingChange }) => {
  const [sourceUrl, setSourceUrl] = useState('');
  const [cropRect, setCropRect] = useState(EMPTY_CROP_RECT);
  const [isApplying, setIsApplying] = useState(false);
  const [error, setError] = useState('');
  const previewFrameRef = useRef(null);
  const dragStateRef = useRef(null);
  const applyingRef = useRef(false);

  useEffect(() => {
    let isCurrent = true;
    setSourceUrl('');
    setCropRect(EMPTY_CROP_RECT);
    setError('');
    readFileAsDataUrl(file)
      .then((url) => {
        if (isCurrent) setSourceUrl(url);
      })
      .catch((readError) => {
        if (isCurrent) setError(readError?.message || '촬영한 이미지를 읽지 못했습니다.');
      });
    return () => {
      isCurrent = false;
    };
  }, [file]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !isApplying) onCancel();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isApplying, onCancel]);

  useEffect(() => {
    const handlePointerMove = (event) => {
      const drag = dragStateRef.current;
      const frame = previewFrameRef.current;
      if (!drag || !frame || isApplying) return;

      const bounds = frame.getBoundingClientRect();
      if (!bounds.width || !bounds.height) return;
      const dx = ((event.clientX - drag.startClientX) / bounds.width) * 100;
      const dy = ((event.clientY - drag.startClientY) / bounds.height) * 100;
      const start = drag.start;
      const startWidth = 100 - start.left - start.right;
      const startHeight = 100 - start.top - start.bottom;
      let next = { ...start };

      if (drag.mode === 'move') {
        next.left = clampPercent(start.left + dx, 0, 100 - startWidth);
        next.top = clampPercent(start.top + dy, 0, 100 - startHeight);
        next.right = 100 - startWidth - next.left;
        next.bottom = 100 - startHeight - next.top;
      } else {
        if (drag.mode.includes('left')) {
          next.left = clampPercent(start.left + dx, 0, 100 - start.right - MIN_CROP_SPAN_PERCENT);
        }
        if (drag.mode.includes('right')) {
          next.right = clampPercent(start.right - dx, 0, 100 - start.left - MIN_CROP_SPAN_PERCENT);
        }
        if (drag.mode.includes('top')) {
          next.top = clampPercent(start.top + dy, 0, 100 - start.bottom - MIN_CROP_SPAN_PERCENT);
        }
        if (drag.mode.includes('bottom')) {
          next.bottom = clampPercent(start.bottom - dy, 0, 100 - start.top - MIN_CROP_SPAN_PERCENT);
        }
      }

      setCropRect(next);
    };

    const stopDragging = () => {
      dragStateRef.current = null;
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', stopDragging);
    window.addEventListener('pointercancel', stopDragging);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', stopDragging);
      window.removeEventListener('pointercancel', stopDragging);
    };
  }, [isApplying]);

  const startCropDrag = (mode, event) => {
    if (isApplying) return;
    event.preventDefault();
    dragStateRef.current = {
      mode,
      startClientX: event.clientX,
      startClientY: event.clientY,
      start: { ...cropRect }
    };
  };

  const applyCrop = async () => {
    if (!sourceUrl || applyingRef.current) return;
    const widthPercent = 100 - cropRect.left - cropRect.right;
    const heightPercent = 100 - cropRect.top - cropRect.bottom;
    if (widthPercent < MIN_CROP_SPAN_PERCENT || heightPercent < MIN_CROP_SPAN_PERCENT) {
      setError('선택 영역이 너무 작습니다. 사각 영역을 넓혀 주세요.');
      return;
    }

    try {
      applyingRef.current = true;
      setIsApplying(true);
      onProcessingChange?.(true);
      setError('');
      const croppedFile = await cropImage(sourceUrl, cropRect, file?.name);
      onApply(croppedFile);
    } catch (cropError) {
      setError(cropError?.message || '이미지 자르기에 실패했습니다.');
      setIsApplying(false);
    } finally {
      applyingRef.current = false;
      onProcessingChange?.(false);
    }
  };

  const selectionCenterX = cropRect.left + ((100 - cropRect.left - cropRect.right) / 2);
  const selectionCenterY = cropRect.top + ((100 - cropRect.top - cropRect.bottom) / 2);

  return (
    <div
      className="answer-sheet-crop-backdrop"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget && !isApplying) onCancel();
      }}
    >
      <section
        className="answer-sheet-crop-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="answer-sheet-crop-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <h2 id="answer-sheet-crop-title">답안지 영역 자르기</h2>
            <p>초록색 선과 손잡이를 움직여 답안지만 남겨 주세요.</p>
          </div>
          <button type="button" onClick={onCancel} disabled={isApplying} aria-label="이미지 자르기 닫기">×</button>
        </header>

        <div className="answer-sheet-crop-preview-wrap">
          {!sourceUrl && !error && <p className="answer-sheet-crop-loading">이미지를 불러오는 중입니다.</p>}
          {sourceUrl && (
            <div className="answer-sheet-crop-preview-frame" ref={previewFrameRef}>
              <img src={sourceUrl} alt="답안지 자르기 미리보기" draggable="false" />
              <div className="answer-sheet-crop-mask top" style={{ height: `${cropRect.top}%` }} />
              <div className="answer-sheet-crop-mask bottom" style={{ height: `${cropRect.bottom}%` }} />
              <div
                className="answer-sheet-crop-mask left"
                style={{ top: `${cropRect.top}%`, bottom: `${cropRect.bottom}%`, width: `${cropRect.left}%` }}
              />
              <div
                className="answer-sheet-crop-mask right"
                style={{ top: `${cropRect.top}%`, bottom: `${cropRect.bottom}%`, width: `${cropRect.right}%` }}
              />
              <div
                className="answer-sheet-crop-focus-window"
                style={{
                  top: `${cropRect.top}%`,
                  right: `${cropRect.right}%`,
                  bottom: `${cropRect.bottom}%`,
                  left: `${cropRect.left}%`
                }}
              />
              <button
                type="button"
                className="answer-sheet-crop-move"
                style={{ left: `${selectionCenterX}%`, top: `${selectionCenterY}%` }}
                onPointerDown={(event) => startCropDrag('move', event)}
                aria-label="선택 영역 이동"
              >
                <FiMove aria-hidden="true" />
              </button>
              {[
                ['top', { top: `${cropRect.top}%`, left: `${selectionCenterX}%` }],
                ['bottom', { bottom: `${cropRect.bottom}%`, left: `${selectionCenterX}%` }],
                ['left', { left: `${cropRect.left}%`, top: `${selectionCenterY}%` }],
                ['right', { right: `${cropRect.right}%`, top: `${selectionCenterY}%` }],
                ['top-left', { top: `${cropRect.top}%`, left: `${cropRect.left}%` }],
                ['top-right', { top: `${cropRect.top}%`, right: `${cropRect.right}%` }],
                ['bottom-left', { bottom: `${cropRect.bottom}%`, left: `${cropRect.left}%` }],
                ['bottom-right', { bottom: `${cropRect.bottom}%`, right: `${cropRect.right}%` }]
              ].map(([position, style]) => (
                <button
                  type="button"
                  key={position}
                  className={`answer-sheet-crop-handle ${position}`}
                  style={style}
                  onPointerDown={(event) => startCropDrag(position, event)}
                  aria-label={`${position} 자르기 경계 조절`}
                />
              ))}
            </div>
          )}
        </div>

        {error && <p className="answer-sheet-crop-error" role="alert">{error}</p>}
        <footer>
          <button type="button" className="cancel" onClick={onCancel} disabled={isApplying}>취소</button>
          <button type="button" className="apply" onClick={applyCrop} disabled={!sourceUrl || isApplying}>
            {isApplying ? '자르는 중…' : '확인'}
          </button>
        </footer>
      </section>
    </div>
  );
};

export default AnswerSheetCropModal;
